import { AlertTriangle, FileSearch, Globe2, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const SelectField = ({ label, value, onChange, children, testId }) => (
  <label className="block">
    <span className="block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">{label}</span>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-400"
      data-testid={testId}
    >
      {children}
    </select>
  </label>
);

export const ResearchBriefForm = ({
  prompt, setPrompt,
  paperType, setPaperType,
  language, setLanguage,
  citationStyle, setCitationStyle,
  targetWords, setTargetWords,
  includeOpenAccess, setIncludeOpenAccess,
  error, loading, access, onSubmit,
}) => (
  <form onSubmit={onSubmit} className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="research-form">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center"><FileSearch className="w-5 h-5" /></div>
      <div>
        <h2 className="text-xl font-semibold text-slate-950">Research brief</h2>
        <p className="text-xs text-slate-500 mt-0.5">Be specific about scope and comparison.</p>
      </div>
    </div>

    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">Research prompt</span>
      <textarea
        value={prompt}
        onChange={(event) => setPrompt(event.target.value)}
        maxLength={3000}
        rows={7}
        placeholder="Example: Review recent approaches to cobalt recovery from copper-cobalt tailings, comparing leaching agents, operating conditions, recoveries, and environmental trade-offs."
        className="w-full rounded-md border border-slate-200 bg-white p-3 text-sm leading-6 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-400 resize-y"
        data-testid="research-prompt"
      />
      <span className="block text-right text-[11px] text-slate-400 mt-1">{prompt.length}/3000</span>
    </label>

    <div className="grid grid-cols-2 gap-4 mt-4">
      <SelectField label="Document" value={paperType} onChange={setPaperType} testId="research-paper-type">
        <option value="literature_review">Literature review</option>
        <option value="journal_article">Journal-style article</option>
        <option value="thesis_chapter">Thesis chapter</option>
        <option value="technical_report">Technical report</option>
      </SelectField>
      <SelectField label="Language" value={language} onChange={setLanguage} testId="research-language">
        <option value="English">English</option>
        <option value="French">Français</option>
      </SelectField>
      <SelectField label="Citation style" value={citationStyle} onChange={setCitationStyle} testId="research-citation-style">
        <option value="APA">APA</option>
        <option value="IEEE">IEEE</option>
        <option value="HARVARD">Harvard</option>
        <option value="VANCOUVER">Vancouver</option>
      </SelectField>
      <SelectField label="Target length" value={targetWords} onChange={setTargetWords} testId="research-target-words">
        <option value="1000">≈ 1,000 words</option>
        <option value="1800">≈ 1,800 words</option>
        <option value="2500">≈ 2,500 words</option>
        <option value="3500">≈ 3,500 words</option>
      </SelectField>
    </div>

    <label className="mt-5 flex items-start gap-3 rounded-lg border border-sky-200 bg-sky-50 p-3.5 cursor-pointer">
      <input
        type="checkbox"
        checked={includeOpenAccess}
        onChange={(event) => setIncludeOpenAccess(event.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
        data-testid="research-include-open-access"
      />
      <span>
        <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-900"><Globe2 className="w-4 h-4 text-sky-700" />Fill gaps with open-access sources</span>
        <span className="block text-xs leading-5 text-slate-600 mt-1">Search OpenAlex only when your library has fewer than eight usable sources. External evidence is clearly labelled.</span>
      </span>
    </label>

    {error && (
      <div className="mt-5 flex gap-2.5 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
      </div>
    )}

    <Button
      type="submit"
      disabled={loading || prompt.trim().length < 20 || (access?.level === "free" && access?.free_draft_used)}
      className="w-full mt-6 h-12 bg-orange-600 hover:bg-orange-700 text-white"
      data-testid="research-generate"
    >
      {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Reading the library…</> : <><Sparkles className="w-4 h-4 mr-2" />Generate grounded draft</>}
    </Button>
    <p className="text-[11px] text-slate-400 text-center mt-3">Runs in the background — typically 40–90 seconds.</p>
  </form>
);
